import { supabase } from '@/lib/supabase';
import type { QuestDefinition, QuestProgress, QuestStage } from '@/types/quests';

// ---------------------------------------------------------------------------
// Reads — direct DB queries via RLS (client can SELECT own rows)
// ---------------------------------------------------------------------------

export async function getQuestProgress(
  characterId: string,
  questId: string,
): Promise<QuestProgress | null> {
  const { data, error } = await supabase
    .from('quest_progress')
    .select('*')
    .eq('character_id', characterId)
    .eq('quest_id', questId)
    .maybeSingle<QuestProgress>();

  if (error && error.code !== 'PGRST116') {
    console.error('Failed to fetch quest progress', error);
  }
  return data ?? null;
}

export function getStageByKey(quest: QuestDefinition, stageKey: string): QuestStage | undefined {
  return quest.stages[stageKey];
}

// ---------------------------------------------------------------------------
// Mutations — all go through the quest-action Edge Function
// ---------------------------------------------------------------------------

interface QuestActionPayload {
  action: string;
  characterId: string;
  questId: string;
  [key: string]: unknown;
}

interface QuestActionResult {
  ok?: boolean;
  error?: string;
  character?: Record<string, unknown>;
  [key: string]: unknown;
}

async function invokeQuestAction(payload: QuestActionPayload): Promise<QuestActionResult> {
  const { data, error } = await supabase.functions.invoke<QuestActionResult>('quest-action', {
    body: payload,
  });

  if (error) {
    console.error('Quest action failed', error);
    return { error: 'Network error. Try again.' };
  }

  return data ?? { error: 'Empty response from server.' };
}

export async function startQuest(
  characterId: string,
  questId: string,
): Promise<QuestProgress | null> {
  const result = await invokeQuestAction({
    action: 'start',
    characterId,
    questId,
  });

  if (result.error) {
    console.error('startQuest error:', result.error);
    return null;
  }

  return result as unknown as QuestProgress;
}

export async function advanceQuestStage(
  characterId: string,
  questId: string,
  expectedStage: number,
  newStage: number,
  stageKey: string,
  choiceLabel?: string,
): Promise<boolean> {
  const result = await invokeQuestAction({
    action: 'advance',
    characterId,
    questId,
    expectedStage,
    newStage,
    stageKey,
    choiceLabel: choiceLabel ?? null,
  });

  if (result.error) {
    console.error('advanceQuestStage error:', result.error);
    return false;
  }
  return true;
}

export async function completeQuest(
  characterId: string,
  questId: string,
  expectedStage: number,
  rewards: { doubloons?: number; xp?: number; marks?: number },
  nextTutorialNum?: number,
  nextQuestId?: string,
): Promise<boolean> {
  const result = await invokeQuestAction({
    action: 'complete',
    characterId,
    questId,
    expectedStage,
    doubloons: rewards.doubloons ?? 0,
    xp: rewards.xp ?? 0,
    marks: rewards.marks ?? 0,
    nextTutorialNum: nextTutorialNum ?? null,
    nextQuestId: nextQuestId ?? null,
  });

  if (result.error) {
    console.error('completeQuest error:', result.error);
    return false;
  }
  return true;
}

export async function resetQuest(characterId: string, questId: string): Promise<boolean> {
  const result = await invokeQuestAction({
    action: 'reset',
    characterId,
    questId,
  });

  if (result.error) {
    console.error('resetQuest error:', result.error);
    return false;
  }
  return true;
}
